// Components/Common/GentleReminders.js
//
// Round REMIND-1 (2026-10-03) — quiet reminders inside the dashboard, no e-mail.
// Owner: "instead of email any pop up window in dashboard … not irritating and should not stop
// anyone's work … remind but silently."
//
// A small card in the bottom-right corner, just above the approvals pill:
//   • approvals — "3 approvals are waiting for you", one link per area;
//   • clock-in  — "We don't see a clock-in for you today" (the server decides who sees it,
//                 see ClockInNudgeService; HR switches it on once punches arrive live).
// It never blocks anything: no backdrop, it takes no keyboard focus, nothing to confirm. Each note
// shows at most once a day per person (remembered in this browser), a few seconds after the page
// opens. It hides itself after 15 seconds, waits while the mouse or keyboard is on it, and has ×.
// HR / IT Admin switch the notes on or off in HR → More → Reminders.

import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../api";
import useAuth from "../../pages/Personal Attendance/useAuth";
import "./GentleReminders.css";

const FIRST_DELAY_MS = 6000;
const SHOW_MS = 15000;
const AFTER_LEAVE_MS = 5000;
const RECHECK_MS = 15 * 60 * 1000;
const STORE_KEY = "ssw.gentleReminders";

// Approval queues only (tasks are work, not someone waiting on a decision).
const APPROVAL_AREAS = ["epo", "fto", "coa", "cr"];
const SHORT_LABEL = { epo: "EPO", fto: "FTO", coa: "COA", cr: "Contract review" };
// Clock-in answers that may change later today: ask again in 15 minutes.
const ASK_AGAIN = new Set(["before-hours", "punch-feed-quiet", "unavailable"]);

const todayKey = () => new Date().toLocaleDateString("en-CA"); // YYYY-MM-DD, local day

function readDone(empId) {
    try {
        const s = JSON.parse(window.localStorage.getItem(STORE_KEY) || "null");
        if (s && s.empId === empId && s.day === todayKey()) return s.done || {};
    } catch { /* storage blocked: treat as nothing shown yet */ }
    return {};
}

function markDone(empId, key) {
    try {
        const done = { ...readDone(empId), [key]: true };
        window.localStorage.setItem(STORE_KEY, JSON.stringify({ empId, day: todayKey(), done }));
    } catch { /* best-effort */ }
}

const GentleReminders = () => {
    const { empId, canAccessModule } = useAuth();
    // The missing-punch window lives on Time & Attendance; people without that page get a plain hint.
    const canOpenTimeAttendance = canAccessModule("time.open");
    const navigate = useNavigate();
    const [items, setItems] = useState([]);
    const [leaving, setLeaving] = useState(false);
    const hideTimer = useRef(null);

    const close = useCallback(() => {
        clearTimeout(hideTimer.current);
        setLeaving(true);
        hideTimer.current = setTimeout(() => { setItems([]); setLeaving(false); }, 250);
    }, []);

    const hideAfter = useCallback((ms) => {
        clearTimeout(hideTimer.current);
        hideTimer.current = setTimeout(close, ms);
    }, [close]);

    // One check: returns true when another check later today could still show something.
    const check = useCallback(async () => {
        if (!empId) return false;
        const done = readDone(empId);
        if (done.approvals && done.clockIn) return false;

        let me;
        try { me = (await api.get("reminders/me")).data; } catch { return true; }

        const found = [];
        let askAgain = false;
        if (!done.clockIn) {
            const c = me?.clockIn;
            if (c?.show) found.push({ key: "clockIn" });
            else if (c && ASK_AGAIN.has(c.reason)) askAgain = true;
            else markDone(empId, "clockIn");
        }
        if (!done.approvals) {
            if (me?.approvalsEnabled) {
                try {
                    const { data } = await api.get("pending-approvals/summary");
                    const areas = (data?.areas || []).filter((a) => APPROVAL_AREAS.includes(a.key) && a.total > 0);
                    if (areas.length) found.push({ key: "approvals", areas });
                    else markDone(empId, "approvals");
                } catch { askAgain = true; }
            } else {
                markDone(empId, "approvals");
            }
        }
        if (found.length) {
            found.forEach((f) => markDone(empId, f.key));
            setLeaving(false);
            setItems(found);
        }
        return askAgain;
    }, [empId]);

    useEffect(() => {
        let stopped = false;
        let timer = null;
        const run = async () => {
            if (stopped) return;
            const again = await check();
            if (!stopped && again) timer = setTimeout(run, RECHECK_MS);
        };
        timer = setTimeout(run, FIRST_DELAY_MS);
        return () => { stopped = true; clearTimeout(timer); };
    }, [check]);

    useEffect(() => {
        if (items.length) hideAfter(SHOW_MS);
    }, [items, hideAfter]);

    useEffect(() => () => clearTimeout(hideTimer.current), []);

    const go = (to) => { close(); navigate(to); };
    const openArea = (a) => {
        api.post("pending-approvals/seen", { area: a.key, cursor: a.cursor }).catch(() => {});
        go(a.link);
    };
    const pause = () => clearTimeout(hideTimer.current);
    const resumeOnBlur = (e) => {
        if (!e.currentTarget.contains(e.relatedTarget)) hideAfter(AFTER_LEAVE_MS);
    };

    // The live region is always there (empty when nothing is shown) so screen readers read the
    // note politely when it appears; an empty host has no size and catches no clicks.
    return (
        <div className="ssw-gentle-host" role="status" aria-live="polite">
        {items.length > 0 && (
        <div
            className={`ssw-gentle${leaving ? " is-leaving" : ""}`}
            onMouseEnter={pause}
            onMouseLeave={() => hideAfter(AFTER_LEAVE_MS)}
            onFocus={pause}
            onBlur={resumeOnBlur}
        >
            <button type="button" className="ssw-gentle__close" aria-label="Dismiss reminder" title="Dismiss" onClick={close}>
                <i className="ri-close-line" aria-hidden="true" />
            </button>
            {items.map((it) => it.key === "clockIn" ? (
                <div className="ssw-gentle__row" key="clockIn">
                    <i className="ri-time-line ssw-gentle__icon" aria-hidden="true" />
                    <div className="ssw-gentle__body">
                        <div className="ssw-gentle__title">We don't see a clock-in for you today.</div>
                        {canOpenTimeAttendance ? (
                            <>
                                <div className="ssw-gentle__text">If you forgot to swipe, you can ask for a missing punch.</div>
                                <div className="ssw-gentle__links">
                                    <button type="button" className="ssw-gentle__link" onClick={() => go("/time-and-attendance?open=missing")}>
                                        Missing punch
                                    </button>
                                </div>
                            </>
                        ) : (
                            <div className="ssw-gentle__text">If you forgot to swipe, please tell HR so they can add it.</div>
                        )}
                    </div>
                </div>
            ) : (
                <div className="ssw-gentle__row" key="approvals">
                    <i className="ri-checkbox-multiple-line ssw-gentle__icon" aria-hidden="true" />
                    <div className="ssw-gentle__body">
                        <div className="ssw-gentle__title">
                            {(() => {
                                const n = it.areas.reduce((s, a) => s + a.total, 0);
                                return `${n > 99 ? "99+" : n} ${n === 1 ? "approval is" : "approvals are"} waiting for you.`;
                            })()}
                        </div>
                        <div className="ssw-gentle__links">
                            {it.areas.map((a) => (
                                <button type="button" key={a.key} className="ssw-gentle__link" onClick={() => openArea(a)}>
                                    {SHORT_LABEL[a.key] || a.label} <span className="ssw-gentle__count">{a.total > 99 ? "99+" : a.total}</span>
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            ))}
        </div>
        )}
        </div>
    );
};

export default GentleReminders;
